import { useAuthenticated, useFloors } from "@gettactic/hooks";
import React from "react";
import {
  ZoneLabelPosition,
  ZoneLink,
  ZoneShape,
  ZoneType,
  ZoneTypeDefinition
} from "../../../../../types";
import { computeZoneAreaPx } from "../../../../../utils/zonePolygon";
import { EditorField, EditorFieldGrid } from "../../../../ui/EditorField";
import { EditorSelect } from "../../../../ui/EditorSelect";
import { PanelSection } from "../../../../ui/PanelShell";
import { Seg } from "../../../../ui/Seg";
import { useMapCanvas } from "../../../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorTool
} from "../../../contexts/MapEditorContext";
import { DragNumberInput } from "../../controls/ScrollDragNumberInput";
import { ShapeSectionOptions } from "../ShapePanel/ShapePanel";
import { ZoneAssignmentSection } from "../ZoneAssignmentSection";

// Rooms and corridors are physical spaces; an open area is an allocation
// drawn over them. A shape tagged with an organisation zone counts as an
// allocation whatever its type, so rooms may straddle its edge.
const ZONE_TYPE_OPTIONS: { value: ZoneType; label: string }[] = [
  { value: "open_area", label: "Open Area (zone)" },
  { value: "room", label: "Room" },
  { value: "corridor", label: "Corridor" },
  { value: "clickable", label: "Clickable (link)" }
];

const SPACE_KIND_LABELS: Record<ZoneType, string> = {
  room: "room",
  open_area: "open area",
  corridor: "corridor",
  clickable: "link"
};

// Radix Select items cannot use an empty-string value, so "(none)" gets a
// sentinel that maps back to `undefined` on change.
const NO_CATEGORY_VALUE = "__none__";

export const ZonePanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape, setActiveTool } = useMapEditorActions();
  const { activeTool } = useMapEditorTool();
  const isDrawingPolygon = activeTool === "zone-polygon";
  const { pixelsPerMeter } = useMapCanvas();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const shape = shapes[0] as ZoneShape;

  const offices = authenticatedUser?.offices?.offices || [];

  // Floors of the office the link points at — the /map view is
  // office + floor scoped, so the link can pin a specific floor.
  const linkOfficeId =
    shape.link?.type === "office" ? shape.link.officeId || null : null;
  const { floors: linkOfficeFloors } = useFloors(linkOfficeId);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    shapes.forEach((s) => {
      updateShape(s.id, { name: e.target.value } as Partial<ZoneShape>);
    });
  };

  const handleLabelPositionChange = (value: string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        labelPosition: value as ZoneLabelPosition
      } as Partial<ZoneShape>);
    });
  };

  const handleLabelFontSizeChange = (value: number) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        labelFontSize: value
      } as Partial<ZoneShape>);
    });
  };

  const handleZoneTypeChange = (value: string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        zoneType: value as ZoneType
      } as Partial<ZoneShape>);
    });
  };

  const handleBorderWidthChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        borderWidth: Number(value)
      } as Partial<ZoneShape>);
    });
  };

  const handleFillOpacityChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        fillOpacity: Number(value) / 100
      } as Partial<ZoneShape>);
    });
  };

  const handleLinkTypeChange = (type: ZoneLink["type"] | "none") => {
    shapes.forEach((s) => {
      const current = (s as ZoneShape).link;
      if (type === "none") {
        updateShape(s.id, { link: undefined } as Partial<ZoneShape>);
      } else if (type === "office") {
        updateShape(s.id, {
          link: {
            type: "office",
            officeId: current?.officeId,
            label: current?.label
          },
          zoneType: "clickable"
        } as Partial<ZoneShape>);
      } else {
        updateShape(s.id, {
          link: { type: "url", url: current?.url || "" },
          zoneType: "clickable"
        } as Partial<ZoneShape>);
      }
    });
  };

  const handleLinkOfficeChange = (officeId: string) => {
    if (officeId === NO_CATEGORY_VALUE) return;
    const office = offices.find((o) => o.id === officeId);
    shapes.forEach((s) => {
      // Changing office invalidates the floor — floors belong to an office.
      updateShape(s.id, {
        link: { type: "office", officeId, label: office?.name }
      } as Partial<ZoneShape>);
    });
  };

  const handleLinkFloorChange = (floorId: string) => {
    shapes.forEach((s) => {
      const current = (s as ZoneShape).link;
      if (current?.type !== "office") return;
      updateShape(s.id, {
        link: {
          ...current,
          floorId: floorId === NO_CATEGORY_VALUE ? undefined : floorId
        }
      } as Partial<ZoneShape>);
    });
  };

  const handleLinkUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        link: { type: "url", url: e.target.value }
      } as Partial<ZoneShape>);
    });
  };

  const handleConvertToPolygon = () => {
    shapes.forEach((s) => {
      const zone = s as ZoneShape;
      if (zone.vertices?.length) return;
      // Seed the polygon from the rectangle corners (already anchored to
      // the bbox top-left), ready for vertex/curve editing on the canvas.
      updateShape(s.id, {
        vertices: [
          { x: 0, y: 0 },
          { x: zone.width, y: 0 },
          { x: zone.width, y: zone.height },
          { x: 0, y: zone.height }
        ],
        edges: [{}, {}, {}, {}]
      } as Partial<ZoneShape>);
    });
  };

  const handleDrawPolygon = () => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        vertices: [],
        edges: []
      } as Partial<ZoneShape>);
    });
    setActiveTool("zone-polygon");
  };

  const handleResetToRectangle = () => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        vertices: undefined,
        edges: undefined
      } as Partial<ZoneShape>);
    });
  };

  const containedCount = shape.containedSpotIds?.length ?? 0;
  const isRegion = shape.zoneType !== "clickable";
  const isRoomWithoutName =
    (shape.zoneType || "open_area") === "room" && !shape.name?.trim();

  const handleZoneChange = (zone: ZoneTypeDefinition | undefined) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        zone_id: zone?.id,
        categoryTypeId: zone?.id
      } as Partial<ZoneShape>);
    });
  };

  return (
    <>
      <PanelSection>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Name</span>
            <EditorField
              label="Name"
              type="text"
              value={shape.name || ""}
              onChange={handleNameChange}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Name Position</span>
            <EditorSelect
              label="Name Position"
              value={shape.labelPosition || "top-left"}
              onValueChange={handleLabelPositionChange}
              options={[
                { value: "top-left", label: "Top Left" },
                { value: "top-center", label: "Top Center" },
                { value: "center", label: "Center" },
                { value: "bottom-left", label: "Bottom Left" },
                { value: "bottom-center", label: "Bottom Center" }
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Name Size</span>
            <DragNumberInput
              size={"sm"}
              value={shape.labelFontSize || 14}
              onChange={handleLabelFontSizeChange}
              min={8}
              max={72}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Zone Type</span>
            <EditorSelect
              label="Zone Type"
              value={shape.zoneType || "open_area"}
              onValueChange={handleZoneTypeChange}
              options={ZONE_TYPE_OPTIONS}
            />
            {isRoomWithoutName ? (
              <p className="text-[11px] text-amber-700">
                Give the room a name: it is listed as a space of this floor.
              </p>
            ) : null}
          </div>
        </div>
      </PanelSection>

      {isRegion ? (
        <ZoneAssignmentSection shape={shape} onChangeZone={handleZoneChange} />
      ) : null}

      <PanelSection label="Shape">
        <div className="flex flex-col gap-2">
          {isDrawingPolygon ? (
            <p className="rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs">
              Click the map to add corners. Double-click, right-click or click
              the first corner to finish.
            </p>
          ) : shape.vertices?.length ? (
            <>
              <p className="text-neutral-500 text-xs">
                Polygon with {shape.vertices.length} corners. Drag the corner
                handles to reshape, or the edge dots to curve an edge.
              </p>
              <button
                type="button"
                className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                onClick={handleDrawPolygon}
              >
                Redraw Polygon
              </button>
              <button
                type="button"
                className="w-full rounded-md bg-neutral-100 px-2 py-1.5 text-neutral-700 text-xs hover:bg-neutral-200"
                onClick={handleResetToRectangle}
              >
                Reset to Rectangle
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                onClick={handleConvertToPolygon}
              >
                Convert to Polygon
              </button>
              <button
                type="button"
                className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                onClick={handleDrawPolygon}
              >
                Draw Polygon
              </button>
            </>
          )}
        </div>
      </PanelSection>

      <PanelSection label="Link">
        <div className="flex flex-col gap-2">
          <Seg<ZoneLink["type"] | "none">
            label="Link"
            value={shape.link?.type || "none"}
            onValueChange={(value) => {
              if (value === (shape.link?.type || "none")) return;
              handleLinkTypeChange(value);
            }}
            options={[
              { value: "none", ariaLabel: "None", label: "None" },
              { value: "office", ariaLabel: "Office Map", label: "Office" },
              { value: "url", ariaLabel: "External URL", label: "URL" }
            ]}
          />
          {shape.link?.type === "office" ? (
            <EditorSelect
              label="Office"
              value={shape.link.officeId || NO_CATEGORY_VALUE}
              onValueChange={handleLinkOfficeChange}
              options={[
                { value: NO_CATEGORY_VALUE, label: "(choose office)" },
                ...offices.map((office) => ({
                  value: office.id,
                  label: office.name
                }))
              ]}
            />
          ) : null}
          {shape.link?.type === "office" && shape.link.officeId ? (
            <EditorSelect
              label="Floor"
              value={shape.link.floorId || NO_CATEGORY_VALUE}
              onValueChange={handleLinkFloorChange}
              options={[
                { value: NO_CATEGORY_VALUE, label: "(default floor)" },
                ...(linkOfficeFloors || []).map((linkFloor) => ({
                  value: linkFloor.id,
                  label: linkFloor.name || linkFloor.id
                }))
              ]}
            />
          ) : null}
          {shape.link?.type === "url" ? (
            <EditorField
              label="URL"
              type="text"
              value={shape.link.url || ""}
              onChange={handleLinkUrlChange}
              placeholder="https://…"
            />
          ) : null}
          {shape.link ? (
            <p className="text-neutral-500 text-xs">
              Viewers can click this zone to open the linked{" "}
              {shape.link.type === "office" ? "office map" : "URL"}.
            </p>
          ) : null}
        </div>
      </PanelSection>

      <PanelSection label="Appearance">
        <EditorFieldGrid>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Border Width</span>
            <DragNumberInput
              size={"sm"}
              value={shape.borderWidth || 2}
              onChange={handleBorderWidthChange}
              min={0}
              max={20}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Fill Opacity (%)</span>
            <DragNumberInput
              size={"sm"}
              value={Math.round((shape.fillOpacity ?? 0.12) * 100)}
              onChange={handleFillOpacityChange}
              min={0}
              max={100}
            />
          </div>
        </EditorFieldGrid>
        {isRegion ? (
          <p className="mt-2 text-neutral-500 text-xs">
            Space: {SPACE_KIND_LABELS[shape.zoneType || "open_area"]}
            {shape.parentShapeId ? " · nested in another space" : ""}
          </p>
        ) : null}
        {containedCount > 0 && (
          <p className="mt-2 text-neutral-500 text-xs">
            Contains {containedCount} spot{containedCount !== 1 ? "s" : ""}
          </p>
        )}
        {pixelsPerMeter ? (
          <p className="mt-2 text-neutral-500 text-xs">
            Area: {(computeZoneAreaPx(shape) / pixelsPerMeter ** 2).toFixed(1)}{" "}
            m²
          </p>
        ) : null}
      </PanelSection>
    </>
  );
};
